'use strict';
exports.__esModule = true;
var treewidget;
(function (treewidget) {
    /**
     * Basic json structure of a tree node. This is basically a Json
     * object, but specified some essential key/value. Needs a
     * transform function if the input json object does not have the
     * essentials.
     *
     * This class is for single tree nodes. The JsonTree class is for
     * whole tree handling. So for single tree node, there is not much
     * restrictions. The tree handler should handle any necessary
     * checkings regarding the whole tree.
     */
    var TreeNode = /** @class */ (function () {
        function TreeNode(_id, name, parent, children, isPlatform, templates, tags, dropdownTags) {
            this._id = _id ? _id : new Date().getTime().toString();
            this.name = name ? name : this._id;
            this.tags = tags ? tags : ['all'];
            this.dropdownTags = dropdownTags ? dropdownTags : [];
            this.parent = parent ? parent : undefined;
            this.children = children ? children : [];
            this.isPlatform = isPlatform;
            this.templates = templates;
            this.role = "a";
            this.permissions = "";
            this.isDataValid = undefined;
            this.isArchiveLoaded = false;
        }
        //================================
        // Add and Edit
        //================================
        /**
         * Create a new node then add the new node as a child of
         * current node. The new child will be placed as the last of
         * the list.
         *
         * @param {string} _id The unique id of the new node. If
         *     passing a null, a unique id will be generated for this
         *     value.
         * @param {string} name This value is the display text for this
         *     node. Same name in children list is not allowed.
         * @param isPlatform {boolean} Whether this node is a platform.
         * @param templates {Array} Array of templates of current node.
         *     Normally one node should only have one template.
         * @param tags {Array} Array of tags.
         * @param dropdownTags {Array} Array of dropdownTags.
         *
         * @return True if added successfully. False otherwise.
         */
        TreeNode.prototype.createNewNodeAsChild = function (_id, name, isPlatform, templates, path, tags, dropdownTags) {
            var newTreeNode = new TreeNode(_id, name, this, [], isPlatform, templates, tags, dropdownTags);
            newTreeNode['path'] = path;
            return this.addNodeAsChild(newTreeNode);
        };
        /**
         * Add a node or subtree to current node. Can be used when
         * trying to insert a node, or cut/copy and paste node/subtree.
         * This function also check children name before finalize the
         * process. <b>Notice:</b> this function will not create a copy
         * of the input node.
         *
         * @param {TreeNode} node A node to add, or the root of a
         *     subtree.
         *
         * @returns {boolean} True if added successfully. False
         *     otherwise.
         */
        TreeNode.prototype.addNodeAsChild = function (node) {
            if (!node) {
                return false;
            }
            if (!this.children) {
                this.children = [];
            }
            if (this.checkChildrenNameAvailability(node.name)) {
                var len = this.children.length;
                this.hasChildren = true;
                return this.children.push(node) == len + 1;
            }
            else {
                return false;
            }
        };
        /**
         * Edit current node. Currently only edit node name.
         *
         * @param newName {string} New name for the node.
         * @returns {boolean} True if success or nothing changed.
         */
        TreeNode.prototype.editNode = function (newName) {
            if (this.checkNodeNameAvailability(newName)) {
                this.name = newName;
                return true;
            }
            else {
                return false;
            }
        };
        /**
         * When try to rename a node, better check if the new name is
         * conflict with any other sibling. Same display name may lead
         * to confusion for user.
         *
         * @param {string} newName New name for this node.
         * @returns {boolean} If new name is good to go, return true.
         *     Otherwise return false.
         */
        TreeNode.prototype.checkNodeNameAvailability = function (newName) {
            if (!newName || !newName.trim()) {
                // Emtpy
                return false;
            }
            else if (newName.trim() === this.name.trim()) {
                // Unchanged
                return true;
            }
            else if (!this.parent) {
                // If changes root name
                return true;
            }
            else {
                // Prevent conflict with siblings
                return this.parent.checkChildrenNameAvailability(newName);
            }
        };
        /**
         * Check if there is an existing name identical to something.
         * To avoid user confusion, better have different display name
         * for each node.
         *
         * @param {string} nameToCheck Any string to check.
         * @returns {boolean} True if this string has no duplication.
         *     False otherwise.
         */
        TreeNode.prototype.checkChildrenNameAvailability = function (nameToCheck) {
            if (!nameToCheck) {
                return false;
            }
            for (var i = 0; i < this.children.length; i++) {
                if (this.children[i].name === nameToCheck) {
                    return false;
                }
            }
            return true;
        };
        /**
         * Find child by id in children array.
         *
         * @param childId {string} Child Id, should be unique.
         * @returns {any} Child node object.
         */
        TreeNode.prototype.getChildById = function (childId) {
            for (var i = 0; i < this.children.length; i++) {
                if (this.children[i]._id == childId) {
                    return this.children[i];
                }
            }
            return undefined;
        };
        //================================
        // Array children
        //================================
        /**
         * Array children of tree node.
         *
         * @param children {Array} List of children of new order.
         * @returns {boolean} True when arraged successfully.
         */
        TreeNode.prototype.arrageChildren = function (children) {
            var result = true;
            if (children && children.length) {
                var newChildrenList = [];
                var childNode = undefined;
                for (var i = 0; i < children.length; i++) {
                    childNode = this.getChildById(children[i]);
                    if (childNode) {
                        newChildrenList.push(childNode);
                    }
                    else {
                        result = false;
                        break;
                    }
                }
                if (result) {
                    this.children = newChildrenList;
                }
            }
            return result;
        };
        //================================
        // Delete
        //================================
        /**
         * Delete a child node of current node.
         *
         * @param {string} nodeId The child node id, specified which
         *     child to delete, search by node id to avoid reference
         *     confusion.
         *
         * @returns {boolean} True if such a node exists. False
         *     otherwise.
         */
        TreeNode.prototype.deleteChildNode = function (nodeId) {
            var toDelete = -1;
            for (var i = 0; i < this.children.length; i++) {
                if (this.children[i]._id === nodeId) {
                    toDelete = i;
                    break;
                }
            }
            if (toDelete > -1) {
                var deletedNodes = this.children.splice(toDelete, 1);
                this.hasChildren = this.children.length > 0;
                return deletedNodes[0]._id === nodeId;
            }
            else {
                return false;
            }
        };
        //================================
        // Platform and Leaf
        //================================
        /**
         * Recursively check if any ancestor of a node is platform.
         * Not include the input node.
         *
         * @returns {TreeNode} The closest platform found or undefined.
         */
        TreeNode.prototype.ancestorIsPlatform = function () {
            if (!this.parent) {
                return undefined;
            }
            if (this.parent.isPlatform) {
                return this.parent;
            }
            else {
                return this.parent.ancestorIsPlatform();
            }
        };
        /**
         * If this node or any of its ancestors is platform.
         *
         * @returns {TreeNode} The closest platform found or undefined.
         */
        TreeNode.prototype.ancestorIsPlatformInclude = function () {
            if (this.isPlatform) {
                return this;
            }
            else {
                return this.ancestorIsPlatform();
            }
        };
        /**
         * Travers through the subtree of input node to see if there
         * is a platform child. Not including the input node. A node
         * may have multiple children which are platforms. If any of
         * these is found, returns true.
         *
         * @returns {TreeNode} The closest platform found or undefined.
         */
        TreeNode.prototype.hasPlatformChild = function () {
            for (var i = 0; i < this.children.length; i++) {
                var node = this.children[i];
                var t = new TreeNode(node._id, node.name, node.parent, node.children, node.isPlatform, node.templates);
                if (t.isPlatform) {
                    return t;
                }
                else {
                    if (t.hasPlatformChild()) {
                        return t;
                    }
                }
            }
            return undefined;
        };
        /**
         * Is this node or any of its children is platform.
         *
         * @returns {TreeNode} The closest platform found or undefined.
         */
        TreeNode.prototype.hasPlatformChildInclude = function () {
            if (this.isPlatform) {
                return this;
            }
            else {
                return this.hasPlatformChild();
            }
        };
        //================================
        // Hierarchy
        //================================
        /**
         * Is current node a leaf node.
         *
         * @returns {boolean} True if it is a leaf.
         */
        TreeNode.prototype.isLeaf = function () {
            return !(this.children && this.children.length > 0) && !this.hasChildren;
        };
        /**
         * Get the path of current node. Get path nodes id only.
         * @returns {string[]}
         */
        TreeNode.prototype.getPath = function () {
            var path = [this._id];
            var node = this.parent;
            while (node) {
                path.unshift(node._id);
                node = node.parent;
            }
            return path;
        };
        TreeNode.prototype.getRootNode = function () {
            if (!this.parent) {
                return this;
            }
            var root = undefined;
            var node = this.parent;
            while (node) {
                root = node;
                node = node.parent;
            }
            return root;
        };
        /**
         * Get the list of schema names.
         * @returns {Array}
         */
        TreeNode.prototype.getSchemaNames = function () {
            var schemaNames = [];
            var rootNode = this.getRootNode();
            if ('univSchema' in rootNode) {
                var s = rootNode['univSchema'];
                for (var k in s)
                    schemaNames.push(k);
            }
            return schemaNames;
        };
        /**
         * Get the breadcrumb of current node.
         * @returns {string}
         */
        TreeNode.prototype.getBreadcrumb = function () {
            var breadcrumb = this.name;
            var node = this.parent;
            while (node) {
                breadcrumb = node.name + " > " + breadcrumb;
                node = node.parent;
            }
            return breadcrumb;
        };
        //================================
        // Access Control
        //================================
        /**
         * Set read only state. Should be a private function.
         *
         * @param state {any} Read only state, can be 'r' or 'rw'.
         * @param includeChildren {boolean} Include all children or
         *     not. True means change include all children.
         */
        TreeNode.prototype.setReadOnlyState = function (state, includeChildren) {
            this.permissions = state;
            if (includeChildren && this.children) {
                for (var i = 0; i < this.children.length; i++) {
                    this.children[i].setReadOnlyState(state, true);
                }
            }
        };
        /**
         * Make a node read only. Can make the change with all child
         * nodes by setting the param to true.
         *
         * @param includeChildren {boolean} True to apply change to
         *     all children.
         */
        TreeNode.prototype.makeReadOnly = function (includeChildren) {
            this.setReadOnlyState('r', includeChildren);
        };
        /**
         * Make a node writable. Same as read only.
         * @param includeChildren {boolean} True to apply change to
         *     all children.
         */
        TreeNode.prototype.makeEditable = function (includeChildren) {
            this.setReadOnlyState('', includeChildren);
        };
        //================================
        // List of leaves
        //================================
        /**
         * Get a list of all leaves that are editable;
         *
         * @returns {Array} A array of editable leaf nodes.
         */
        TreeNode.prototype.getEditableLeaves = function () {
            var rs = [];
            if (this.children && this.children.length) {
                for (var i = 0; i < this.children.length; i++) {
                    rs = rs.concat(this.children[i].getEditableLeaves());
                }
            }
            else {
                if (this.permissions != 'r') {
                    rs.push(this);
                }
            }
            return rs;
        };
        /**
         * Get a list of all leaves that are not dataValid.
         *
         * @returns {Array} A array of dataInvalid nodes under this node.
         */
        TreeNode.prototype.getDataInvalidLeaves = function () {
            var rs = [];
            if (this.children && this.children.length) {
                for (var i = 0; i < this.children.length; i++) {
                    rs = rs.concat(this.children[i].getDataInvalidLeaves());
                }
            }
            else {
                if (this.isDataValid === false) {
                    rs.push(this);
                }
            }
            return rs;
        };
        /**
         * Get a list of all leaves.
         *
         * @returns {Array} A array of all nodes under this node.
         */
        TreeNode.prototype.getAllLeaves = function () {
            var rs = [];
            if (this.children && this.children.length) {
                for (var i = 0; i < this.children.length; i++) {
                    rs = rs.concat(this.children[i].getAllLeaves());
                }
            }
            else {
                rs.push(this);
            }
            return rs;
        };
        TreeNode.prototype.getPendingLoadingNodes = function () {
            var rs = [];
            if (this.children && this.children.length) {
                for (var i = 0; i < this.children.length; i++) {
                    rs = rs.concat(this.children[i].getPendingLoadingNodes());
                }
            }
            else if (!this.isLeaf()) {
                rs.push(this);
            }
            return rs;
        };
        //================================
        // List of available templates
        //================================
        /**
         * Get a list of templates in this node and subtree. The list
         * contains only unique template names.
         *
         * @return {Array<string>} All available template names.
         */
        TreeNode.prototype.getAllTemplates = function () {
            var rs = [];
            var temps = this.templates ? [this.templates[0]] : [];
            if (this.children && this.children.length) {
                this.children.forEach(function (child) {
                    temps = temps.concat(child.getAllTemplates());
                });
            }
            temps.forEach(function (temp) {
                if (rs.indexOf(temp) < 0) {
                    rs.push(temp);
                }
            });
            return rs.sort();
        };
        TreeNode.prototype.isVisible = function () {
            var currentNode = this;
            while (currentNode) {
                if (currentNode.hide) {
                    return false;
                }
                currentNode = currentNode.parent;
            }
            return true;
        };
        return TreeNode;
    }());
    treewidget.TreeNode = TreeNode;
})(treewidget || (treewidget = {}));
exports["default"] = treewidget;
//# sourceMappingURL=tree-node.js.map


// WEBPACK FOOTER //
// ./app/widgets/navigation/tree/app/js/logic/tree-node.js